import { companyDb } from './companyContext';
import type { LocalExpense, LocalPurchase, LocalSale } from './db';

function inRange(iso: string, from?: string, to?: string): boolean {
  const d = iso.slice(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

export async function localDailySummary() {
  const today = new Date().toISOString().slice(0, 10);
  const sales = await companyDb().local_sales.filter((s) => s.createdAt.startsWith(today)).toArray();
  const expenses = await companyDb().local_expenses.filter((e) => e.createdAt.startsWith(today)).toArray();
  let total_sales = 0;
  let item_sales = 0;
  let service_sales = 0;
  for (const s of sales) {
    total_sales += s.net_total || 0;
    for (const line of s.items) {
      const amt = Number(line.amount) || 0;
      if (line.item_type === 'Service') service_sales += amt;
      else item_sales += amt;
    }
  }
  const total_expenses = expenses.reduce((a, e) => a + (e.amount || 0), 0);
  return {
    total_sales,
    invoice_count: sales.length,
    item_sales,
    service_sales,
    total_expenses,
    source: 'local' as const,
  };
}

export async function localSaleRegister(from: string, to: string) {
  const sales = await companyDb().local_sales
    .filter((s) => inRange(s.createdAt, from, to))
    .toArray();
  return sales.map((s) => ({
    name: s.serverInvoiceName || s.offlineInvoiceNo,
    posting_date: s.createdAt.slice(0, 10),
    customer: s.customer,
    customer_name: s.customerName,
    net_total: s.net_total,
    paid_amount: s.paid_amount,
    status: s.status,
    discount_amount: s.discount_amount,
    tax_amount: s.tax_amount,
    offline: !s.serverInvoiceName,
  }));
}

export async function localSaleSummary(from: string, to: string) {
  const rows = await localSaleRegister(from, to);
  const total = rows.reduce((a, r) => a + (r.net_total || 0), 0);
  const paid = rows.reduce((a, r) => a + (r.paid_amount || 0), 0);
  return {
    invoice_count: rows.length,
    total_sales: total,
    total_paid: paid,
    total_outstanding: Math.max(0, total - paid),
    rows,
  };
}

export async function localPartySaleSummary(from: string, to: string) {
  const rows = await localSaleRegister(from, to);
  const map = new Map<string, { customer: string; customer_name?: string; invoices: number; total: number; paid: number }>();
  for (const r of rows) {
    const key = r.customer || 'unknown';
    const cur = map.get(key) || { customer: key, customer_name: r.customer_name, invoices: 0, total: 0, paid: 0 };
    cur.invoices += 1;
    cur.total += r.net_total || 0;
    cur.paid += r.paid_amount || 0;
    map.set(key, cur);
  }
  return Array.from(map.values());
}

export async function localItemsSaleSummary(from: string, to: string) {
  const sales = await companyDb().local_sales.filter((s) => inRange(s.createdAt, from, to)).toArray();
  const map = new Map<string, { item: string; item_name: string; qty: number; amount: number }>();
  for (const s of sales) {
    for (const line of s.items) {
      if (line.item_type === 'Service') continue;
      const key = String(line.item || line.item_name || '');
      const cur = map.get(key) || {
        item: key,
        item_name: String(line.item_name || key),
        qty: 0,
        amount: 0,
      };
      cur.qty += Number(line.quantity) || 0;
      cur.amount += Number(line.amount) || 0;
      map.set(key, cur);
    }
  }
  return Array.from(map.values());
}

export async function localServicesAnalysis(from: string, to: string) {
  const sales = await companyDb().local_sales.filter((s) => inRange(s.createdAt, from, to)).toArray();
  const map = new Map<string, { item: string; item_name: string; qty: number; amount: number }>();
  for (const s of sales) {
    for (const line of s.items) {
      if (line.item_type !== 'Service') continue;
      const key = String(line.item || line.item_name || '');
      const cur = map.get(key) || {
        item: key,
        item_name: String(line.item_name || key),
        qty: 0,
        amount: 0,
      };
      cur.qty += Number(line.quantity) || 0;
      cur.amount += Number(line.amount) || 0;
      map.set(key, cur);
    }
  }
  return Array.from(map.values());
}

export async function localPurchaseRegister(from: string, to: string) {
  const rows = await companyDb().local_purchases.filter((p) => inRange(p.createdAt, from, to)).toArray();
  return rows.map((p: LocalPurchase) => ({
    name: p.serverName || p.clientOfflineId,
    invoice_date: p.createdAt.slice(0, 10),
    supplier: p.supplier,
    supplier_name: p.supplierName,
    net_total: p.net_total,
    paid_amount: p.paid_amount,
    balance: Math.max(0, (p.net_total || 0) - (p.paid_amount || 0)),
    offline: !p.serverName,
  }));
}

export async function localPurchaseSummary(from: string, to: string) {
  const rows = await localPurchaseRegister(from, to);
  return {
    invoice_count: rows.length,
    total_purchases: rows.reduce((a, r) => a + (r.net_total || 0), 0),
    total_paid: rows.reduce((a, r) => a + (r.paid_amount || 0), 0),
    rows,
  };
}

export async function localSupplierSummary(from: string, to: string) {
  const rows = await localPurchaseRegister(from, to);
  const map = new Map<string, { supplier: string; supplier_name?: string; invoices: number; total: number; paid: number }>();
  for (const r of rows) {
    const key = r.supplier || 'unknown';
    const cur = map.get(key) || { supplier: key, supplier_name: r.supplier_name, invoices: 0, total: 0, paid: 0 };
    cur.invoices += 1;
    cur.total += r.net_total || 0;
    cur.paid += r.paid_amount || 0;
    map.set(key, cur);
  }
  return Array.from(map.values());
}

export async function localExpenseTotal(from: string, to: string) {
  const rows = await companyDb().local_expenses.filter((e) => inRange(e.createdAt, from, to)).toArray();
  return {
    count: rows.length,
    total: rows.reduce((a: number, e: LocalExpense) => a + (e.amount || 0), 0),
    rows,
  };
}

export async function localRecentSales(limit = 20): Promise<LocalSale[]> {
  return companyDb().local_sales.orderBy('createdAt').reverse().limit(limit).toArray();
}

export async function localLowStock(threshold = 5) {
  const stock = await companyDb().local_stock.toArray();
  const items = await companyDb().catalog_items.toArray();
  const nameMap = new Map(items.map((i) => [i.name, i.item_name]));
  return stock
    .filter((s) => s.qty <= threshold)
    .map((s) => ({
      item: s.item,
      item_name: nameMap.get(s.item) || s.item,
      variant: s.variant,
      qty: s.qty,
    }));
}

export async function localRecoveryReceipts(from: string, to: string) {
  const sales = await companyDb().local_sales.filter((s) => inRange(s.createdAt, from, to)).toArray();
  const rows: Array<{ invoice: string; date: string; customer?: string; method?: string; amount: number }> = [];
  for (const s of sales) {
    for (const p of s.payments || []) {
      rows.push({
        invoice: s.serverInvoiceName || s.offlineInvoiceNo,
        date: s.createdAt.slice(0, 10),
        customer: s.customerName || s.customer,
        method: String(p.payment_method || ''),
        amount: Number(p.amount) || 0,
      });
    }
  }
  return rows;
}
